import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import { useTranslation } from 'react-i18next'
import { ChevronDown, UserRoundCog } from 'lucide-react'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { Link } from 'react-router-dom'
import EventAvatar from 'app/frontend/base/components/EventAvatar'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { keyBy } from 'lodash'
import { Badge } from '@/components/ui/badge'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import { CharacterClassIcon } from 'app/frontend/base/components'
import { useVisibleTeamsForUser } from 'app/frontend/base/helpers'
dayjs.extend(RelativeTime)

const EventNavigationMenu = () => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()

  const filteredAndSortedTeamReferences = useVisibleTeamsForUser(user)
  const filteredAndSortedUpcomingEvents = user?.upcomingEvents.filter(
    (eventReference) =>
      filteredAndSortedTeamReferences.some(
        (teamReference) => teamReference.id === eventReference.teamId,
      ),
  )

  if (!user) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <span className='flex gap-1 items-center cursor-pointer ml-2 mr-2'>
          <span className='text-sm'>{t(`header.events`)}</span>
          <ChevronDown
            width={12}
            className='ml-0.5 mt-1 opacity-70 hidden max-[420px]:block sm:block'
          />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {filteredAndSortedUpcomingEvents?.map((eventReference, i) => {
          const teamReference = user.teamReferences.find(
            (ref) => ref.id === eventReference.teamId,
          )

          const participants = keyBy(
            eventReference.participantReferences,
            'characterId',
          )
          const characterReference = user.characterReferences.find(
            (ref) => participants[ref.id.toString()],
          )

          if (!teamReference || !characterReference) return null

          const amountPresent = eventReference.participantReferences.filter(
            (ref) => ref.status === 'present',
          ).length

          return (
            <div key={i}>
              <Link to={`${teamReference.path}/events/${eventReference.id}`}>
                <DropdownMenuItem>
                  <div className='w-full'>
                    <div className='flex items-center gap-2 w-full'>
                      <EventAvatar
                        eventReference={eventReference}
                        className='h-10 w-10'
                      />
                      <div className='w-full'>
                        <div className='flex items-center justify-between gap-4'>
                          <span className='capitalize'>
                            {eventReference.title ??
                              eventReference.primaryActivity.instance?.name ??
                              eventReference.primaryActivity.activityType}
                          </span>

                          <Badge variant='outline' className='capitalize'>
                            {eventReference.primaryActivity.difficulty.replace(
                              '_',
                              ' ',
                            )}
                          </Badge>
                        </div>

                        <div className='opacity-70 font-normal text-[0.7rem] mt-0 capitalize flex justify-between gap-4'>
                          <span>
                            {dayjs(eventReference.startsAt).format(
                              'dddd, MMM D, HH:mm',
                            )}{' '}
                            - {dayjs(eventReference.endsAt).format('HH:mm')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className='mt-2 flex items-center gap-4'>
                      <Badge
                        variant='outline'
                        className='flex items-center gap-2'
                      >
                        <CharacterClassIcon
                          className='ml-[-8px]'
                          size='small'
                          characterClass={characterReference.characterClass}
                        />
                        <span>{characterReference.name}</span>
                        <EventStatusIcon
                          status={participants[characterReference.id].status}
                        />
                      </Badge>
                      <span className={`flex gap-1 text-xs opacity-70`}>
                        <span>{amountPresent}</span>
                        <span>/</span>
                        <span>
                          {eventReference.participantReferences.length}
                        </span>
                      </span>

                      <span className='grow text-right text-xs'>
                        {dayjs(eventReference.startsAt).isBefore(dayjs())
                          ? 'started '
                          : ''}
                        {dayjs(eventReference.startsAt).fromNow()}
                      </span>
                    </div>
                  </div>
                </DropdownMenuItem>
              </Link>

              <DropdownMenuSeparator />
            </div>
          )
        })}

        {!filteredAndSortedUpcomingEvents ||
        filteredAndSortedUpcomingEvents.length === 0 ? (
          <>
            <DropdownMenuLabel className='text-center font-normal max-w-[20rem]'>
              {t('header.no-upcoming-events')}
            </DropdownMenuLabel>

            {window.location.pathname !== '/profile/characters' && (
              <>
                <DropdownMenuSeparator />
                <Link to='/profile/characters'>
                  <DropdownMenuItem className='w-full flex items-center justify-center gap-2'>
                    <UserRoundCog size={14} /> {t('header.profile')}
                  </DropdownMenuItem>
                </Link>
              </>
            )}
          </>
        ) : (
          <DropdownMenuItem>View all upcoming events</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default EventNavigationMenu
